import React, { useState, useMemo } from 'react';
import { css } from '@emotion/react';
import { SecondaryButton } from '@/styles/blocks';
import { colors } from '@/styles';
import { OnwardCheckbox } from '@/components/ShipmentForm/blocks';
import { Grid } from '@material-ui/core';
import { ResponsiveSidebarDialog, StickyModalActions, ModalContent, PrimaryButton } from '@/styles/blocks';

import { ItemCheckbox, Body1, Header1, DateTimePicker } from '../Crossdocking/blocks';
import { genAttributes } from '@onward-delivery/core';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { buildTrackingEvents, itemHasEvent, resolveItemEventType } from '@/utilities/trackingEvents';

const ChecklistModal = ({
    open,
    order,
    content,
    items = [],
    timestamps = [],
    itemEvent,
    // Set only where a return item's meaning inverts — see Crossdocking/itemEvents.js.
    returnItemEvent,
    itemsHelperText,
    callbacks,
}) => {
    const [override, setSelected] = useState({});
    const [updates, setUpdates] = useState({});

    // Intangible items are informational only (no physical footprint), so they are never checked
    // off: they are excluded from the list and from item tracking events.
    const tangibleItems = useMemo(() => items.filter((item) => !item.intangible), [items]);

    // Pre-check against the resolved event, or a return still owed its event reads as already done.
    const selected = useMemo(() => {
        return {
            ...Object.fromEntries(
                tangibleItems.map((item) => [
                    item.item_id,
                    itemHasEvent(item, resolveItemEventType(item, itemEvent, returnItemEvent)),
                ])
            ),
            ...override,
        };
    }, [override, tangibleItems, itemEvent, returnItemEvent]);

    const updated = useMemo(() => {
        return {
            ...Object.fromEntries(timestamps.map(({ field }) => [field, order?.[field]])),
            ...updates,
        };
    }, [order, timestamps, updates]);

    // Item checkboxes are a record of what physically moved, not a completion gate: a step may close
    // with some — or none — of its items checked, and only the checked ones get tracking events.
    const isValid = useMemo(() => {
        return timestamps.every(({ field, required }) => !required || !!updated[field]);
    }, [timestamps, updated]);

    const tz = useMemo(() => {
        const { zip } = genAttributes(order);
        return zipcode_to_timezone.lookup(order[zip]) || 'America/New_York';
    }, [order]);

    return (
        <ResponsiveSidebarDialog
            open={open}
            onClose={() => {
                setSelected({});
                callbacks.onClose();
            }}
        >
            <ModalContent
                css={css`
                    width: 100%;
                    min-width: 600px;
                `}
            >
                {content}
                {!!itemEvent && tangibleItems.length > 0 ? (
                    <>
                        <Grid
                            direction="row"
                            container
                            css={css`
                                margin-top: 30px;
                                margin-bottom: 4px;
                            `}
                        >
                            <Header1>Items</Header1>
                        </Grid>
                        {!!itemsHelperText ? (
                            <Grid
                                direction="row"
                                container
                                css={css`
                                    margin-bottom: 8px;
                                    color: ${colors.greys[0]};
                                `}
                            >
                                <Body1>{itemsHelperText}</Body1>
                            </Grid>
                        ) : null}
                        <Grid direction="row" container>
                            <Grid direction="column" container>
                                <Grid
                                    direction="row"
                                    container
                                    item
                                    css={css`
                                        margin-bottom: 8px;
                                    `}
                                >
                                    <Grid
                                        direction="column"
                                        container
                                        css={css`
                                            flex: 0;
                                            flex-basis: 0;
                                            justify-content: center;
                                        `}
                                    >
                                        <OnwardCheckbox
                                            checked={tangibleItems
                                                .map((item) => item.item_id)
                                                .every((id) => selected[id])}
                                            onChange={() => {
                                                setSelected((prev) => {
                                                    const ids = tangibleItems.map((item) => item.item_id);
                                                    const checked = !ids.every((id) => selected[id]);

                                                    return {
                                                        ...prev,
                                                        ...Object.fromEntries(
                                                            ids.map((id) => [id, checked ? true : false])
                                                        ),
                                                    };
                                                });
                                            }}
                                        />
                                    </Grid>
                                    <Grid
                                        direction="column"
                                        container
                                        css={css`
                                            flex: 1;
                                            flex-basis: 0;
                                            justify-content: center;
                                        `}
                                    >
                                        <Header1>Select All</Header1>
                                    </Grid>
                                </Grid>

                                {tangibleItems.map((item) => {
                                    return (
                                        <ItemCheckbox
                                            key={item.item_id}
                                            item={item}
                                            checked={selected[item.item_id]}
                                            callbacks={{
                                                onCheck: (id) =>
                                                    setSelected((prev) => {
                                                        return {
                                                            ...prev,
                                                            [id]: !prev[id],
                                                        };
                                                    }),
                                            }}
                                        />
                                    );
                                })}
                            </Grid>
                        </Grid>
                    </>
                ) : null}
                <Grid
                    direction="row"
                    container
                    css={css`
                        margin-top: 30px;
                    `}
                >
                    <Grid direction="column" container>
                        {timestamps.map((timestamp) => (
                            <Grid
                                key={timestamp.field}
                                direction="row"
                                container
                                css={css`
                                    margin-bottom: 20px;
                                `}
                            >
                                <DateTimePicker
                                    dateLabel={`${timestamp.name || 'Date'}`}
                                    tz={tz}
                                    timestamp={updated[timestamp.field]}
                                    onChange={(date) =>
                                        setUpdates((prev) => ({
                                            ...prev,
                                            [timestamp.field]: date ? date.toISOString() : null,
                                        }))
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </ModalContent>
            <StickyModalActions>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                    `}
                >
                    <SecondaryButton
                        onClick={() => {
                            setSelected({});
                            callbacks.onClose();
                        }}
                    >
                        Cancel
                    </SecondaryButton>
                </Grid>
                <Grid
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                    `}
                >
                    <PrimaryButton
                        disabled={!isValid}
                        onClick={() => {
                            setSelected({});
                            const trackingEvents = buildTrackingEvents({
                                items: tangibleItems.filter((item) => !!selected[item.item_id]),
                                eventType: itemEvent,
                                returnEventType: returnItemEvent,
                                skipExisting: true,
                            });
                            callbacks.onConfirm(updated, trackingEvents);
                        }}
                    >
                        Confirm
                    </PrimaryButton>
                </Grid>
            </StickyModalActions>
        </ResponsiveSidebarDialog>
    );
};

export default ChecklistModal;
